import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, Input, Label, Text } from "@andrum/ui";
import { signInWithMagicLink } from "@/features/auth/api/auth-api";
import { isAuthEnforced } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

/**
 * Extra auth under email/password: magic link only.
 * No Google / Apple / GitHub in the product UI.
 */
export function AuthMethods({
  mode,
  compact = false,
}: {
  mode: "login" | "signup";
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (!isAuthEnforced()) {
    return null;
  }

  const runMagic = async () => {
    setError(null);
    setInfo(null);
    setBusy(true);
    const result = await signInWithMagicLink(email);
    setBusy(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setInfo(
      mode === "signup" ? t("auth.magicSentSignup") : t("auth.magicSentLogin")
    );
  };

  return (
    <div className={cn(compact ? "mt-3 space-y-2.5" : "mt-8 space-y-5")}>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <Text variant="caption">{t("auth.orContinue")}</Text>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Field {...(compact ? { className: "!gap-1" } : {})}>
        <Label
          htmlFor={`magic-${mode}`}
          {...(compact ? { className: "text-xs" } : {})}
        >
          {t("auth.magicLink")}
        </Label>
        <Input
          id={`magic-${mode}`}
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="du@exempel.se"
          {...(compact ? { className: "min-h-10" } : {})}
        />
      </Field>
      <Button
        fullWidth
        variant="soft"
        loading={busy}
        disabled={busy}
        {...(compact ? { className: "min-h-10" } : {})}
        onClick={() => void runMagic()}
      >
        {t("auth.sendMagicLink")}
      </Button>

      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {info ? (
        <p className="text-xs text-secondary" role="status">
          {info}
        </p>
      ) : null}

      {!compact ? (
        <Text variant="caption" className="text-center leading-relaxed">
          {t("auth.methodsHintNoSocial")}
        </Text>
      ) : null}
    </div>
  );
}
